import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import {
  resetForms,
  selectFormAiFeatures,
  selectFormAiFeaturesForCurrentMeetingTemplate,
  selectFormEventsForPolling,
  addFormAiAgentResults,
  setFormAiAgentResultsLoading,
  setFormAiFeatures,
  setFormAiFeaturesLoading,
  setFormEventsForPolling,
  setFormEventsForPollingLoading,
  setFormScorecards,
  setFormScorecardsLoading,
} from '../../../../../../store/meetingSummary/forms'
import { useLazyGetFeaturesQuery } from '../../../../../../store/ai/ai'
import { useLazyGetAllScorecardsQuery } from '../../../../../../store/ai/scorecard'
import { AIAgentType } from '../../../../../../models/AIAgent'
import { useLazyListAiAgentResultQuery } from '../../../../../../store/ai/aiAgentResult'
import { useLazyListRoomEventsQuery } from '../../../../../../store/media/media'

export const FormInitialization = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)

  useInitializeForm(accountId, roomId, sessionId)

  return null
}

const useInitializeForm = (accountId: string, roomId: string, sessionId: string) => {
  useInitializeFormAiFeatures(accountId, roomId, sessionId)
  useInitializeFormEventsForPolling(accountId, roomId, sessionId)
  useInitializeFormScorecards(accountId)
  useInitializeFormResults(accountId, roomId, sessionId)
  useCleanupForms()
}

const useInitializeFormAiFeatures = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetAiFeatures] = useLazyGetFeaturesQuery()

  useEffect(() => {
    const fetchFormAiFeatures = async () => {
      dispatch(setFormAiFeaturesLoading(true))
      try {
        const { data: formAiFeatures } = await triggerGetAiFeatures(
          {
            accountId,
            pageRequest: { page: 0, size: 500 },
            type: 'scorecard',
            includeRestricted: true,
          },
          false,
        ).unwrap()
        dispatch(setFormAiFeatures(formAiFeatures))
      } finally {
        dispatch(setFormAiFeaturesLoading(false))
      }
    }
    fetchFormAiFeatures()
  }, [dispatch, accountId, roomId, sessionId, triggerGetAiFeatures])
}

const useInitializeFormEventsForPolling = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const eventsForPolling = useAppSelector(selectFormEventsForPolling)

  const [triggerGetRoomEvents] = useLazyListRoomEventsQuery()

  useEffect(() => {
    const fetchMeetingSummaryEvents = async () => {
      if (eventsForPolling.length) {
        return
      }
      dispatch(setFormEventsForPollingLoading(true))
      try {
        const { data: roomEvents } = await triggerGetRoomEvents(
          {
            accountId,
            roomId,
            sessionId,
            pageRequest: { page: 0, size: 500 },
            types: ['aiFeatureExecutionFinished', 'userAllocationUpdated'],
          },
          true,
        )
        if (!roomEvents?.data || !roomEvents.data.length) {
          return
        }
        dispatch(setFormEventsForPolling(roomEvents.data))
      } finally {
        dispatch(setFormEventsForPollingLoading(false))
      }
    }
    fetchMeetingSummaryEvents()
  }, [dispatch, accountId, roomId, sessionId, triggerGetRoomEvents, eventsForPolling])
}

const useInitializeFormScorecards = (accountId: string) => {
  const dispatch = useAppDispatch()
  const formAiFeatures = useAppSelector(selectFormAiFeatures)
  const [triggerGetScorecards] = useLazyGetAllScorecardsQuery()

  useEffect(() => {
    if (!formAiFeatures.length) {
      return
    }
    const fetchFormScoreCards = async () => {
      const aiFeatureIds = formAiFeatures.map((aiFeature) => aiFeature.id)
      dispatch(setFormScorecardsLoading(true))

      try {
        const { data: scorecardsData } = await triggerGetScorecards(
          {
            accountId,
            aiFeatureIds: aiFeatureIds.join(','),
          },
          false,
        ).unwrap()
        dispatch(setFormScorecards(scorecardsData))
      } catch (e: unknown) {
        console.error(e)
      } finally {
        dispatch(setFormScorecardsLoading(false))
      }
    }
    fetchFormScoreCards()
  }, [dispatch, accountId, formAiFeatures, triggerGetScorecards])
}

const useInitializeFormResults = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const formAiFeatures = useAppSelector(selectFormAiFeaturesForCurrentMeetingTemplate)

  const [triggerListAiAgentResults] = useLazyListAiAgentResultQuery()

  useEffect(() => {
    const getFormAiAgentResults = async () => {
      dispatch(setFormAiAgentResultsLoading(true))
      try {
        if (!formAiFeatures.length) {
          return
        }
        const formResults = await triggerListAiAgentResults(
          {
            accountId,
            type: AIAgentType.FORM,
            resultKey: `${roomId}|${sessionId}`,
            partialKeyMatch: true,
          },
          false,
        ).unwrap()
        dispatch(addFormAiAgentResults(formResults))
      } catch (e: unknown) {
        console.error(e)
      } finally {
        dispatch(setFormAiAgentResultsLoading(false))
      }
    }
    getFormAiAgentResults()
  }, [dispatch, accountId, roomId, sessionId, formAiFeatures, triggerListAiAgentResults])
}

const useCleanupForms = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetForms())
    }
  }, [dispatch])
}
